import { useState, useCallback } from 'react'
import {
  DialogContentText,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import StyledLink from 'components/StyledLink'
import { useLazyLoadQuery, graphql } from 'react-relay'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { NotifyAboutJob_JobsQuery } from '__generated__/NotifyAboutJob_JobsQuery.graphql'
import { useRouter } from 'next/router'
import { NotifyAboutJob_Mutation } from '__generated__/NotifyAboutJob_Mutation.graphql'
import { useFragment } from 'react-relay/hooks'
import { NotifyAboutJob_User$key } from '__generated__/NotifyAboutJob_User.graphql'
import { arrayFilterNonNullable } from 'services/array'
import { Nullable } from 'types'

interface INotifyAboutJobProps {
  open: boolean
  userFragmentRef: Nullable<NotifyAboutJob_User$key>
  onClose: () => void
  afterSend?: () => void
}

const NotifyAboutJob = (props: INotifyAboutJobProps) => {
  const { open, onClose, userFragmentRef, afterSend } = props
  const theme = useTheme()
  const [jobId, setJobId] = useState<string | null>(null)

  const freelancer = useFragment(graphql`
    fragment NotifyAboutJob_User on User {
      id
      firstName
    }
  `, userFragmentRef)

  // TODO: load all jobs, not just the first 20 items
  // TODO: preloadedQuery or fragment
  const data = useLazyLoadQuery<NotifyAboutJob_JobsQuery>(graphql`
    query NotifyAboutJob_JobsQuery {
      currentUser {
        allJobs(first: 20, filters: { status: opened }) {
          edges {
            node {
              id
              title
            }
          }
        }
      }
    }
  `, {}, {
    fetchPolicy: 'store-and-network',
  })
  const jobs = data?.currentUser?.allJobs?.edges?.map(e => e?.node).filter(arrayFilterNonNullable) || []

  const router = useRouter()
  const { execute: sendNotification, loading } = useQuickCommit<NotifyAboutJob_Mutation>(graphql`
    mutation NotifyAboutJob_Mutation($input: SendJobOpportunityInput!) {
      sendJobOpportunity(input: $input) {
        user {
          id
          
          canNotify # this should be changed to false after the mutation
        }
        
        jobApplication {
          status
        }
      }
    }
  `)

  const { showSnackbarMessage } = useSnackbar()

  const notify = useCallback(async () => {
    if (!freelancer) return

    const userId = freelancer.id

    if (jobId) {
      const result = await sendNotification({
        input: {
          userId,
          jobId,
        },
      })
      if (result?.sendJobOpportunity?.user?.id) {
        showSnackbarMessage('Notification email sent', { level: 'info' })
      }
    } else {
      router.push('/hire/jobs/new/details')
    }

    onClose()

    if (afterSend) afterSend()
  }, [freelancer, jobId, onClose, afterSend, sendNotification, showSnackbarMessage, router])

  return (
    <Dialog open={open} onClose={onClose} data-cy="notify-freelancer-dialog">
      <DialogTitle>Notify {freelancer?.firstName}</DialogTitle>

      <DialogContent>
        <DialogContentText>
          Choose a Job. {freelancer?.firstName} will be notified about it and invited to send a job application.
        </DialogContentText>
      </DialogContent>

      <DialogContent>
        {jobs.length > 0 && (
          <Select value={jobId} onChange={e => setJobId(e.target.value as string)} fullWidth data-cy="select-job">
            {jobs.length === 0 && (
              <MenuItem value={0}>
                ...
              </MenuItem>
            )}

            {jobs.map(job => (
              <MenuItem value={job.id} key={job.id}>
                {job.title}
              </MenuItem>
            ))}
          </Select>
        )}

        <Button
          fullWidth
          variant="outlined"
          component={StyledLink}
          href="/hire/jobs/new/details"
          style={{ marginTop: theme.spacing(2) }}
        >
          Create New Job
        </Button>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} data-cy="close">Close</Button>
        <Button variant="contained" color="primary" onClick={notify} disabled={!jobId || loading} data-cy="notify">Notify</Button>
      </DialogActions>
    </Dialog>
  )
}
export default NotifyAboutJob
